import React from 'react';
import {
  Box, Typography, Paper, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Stack, Chip, Card, CardContent, FormControl, InputLabel, Select, MenuItem,
  Checkbox, TablePagination, CircularProgress, Grid
} from '@mui/material';
import {
  Add, Delete, Edit, Visibility, VisibilityOutlined, EditOutlined, DeleteOutlined, People, Search,
  AccountBalance, TrendingUp
} from '@mui/icons-material';

const PERIOD_LABELS = {
  month: 'μήνα',
  '3m': '3 μηνών',
  '6m': '6 μηνών',
  '12m': '12 μηνών',
  all: 'συνόλου',
};

export default function SuppliersTab({
  suppliers,
  supplierCategories,
  selectedSuppliers,
  supplierSearchTerm,
  filterSupplierCategory,
  supplierPeriod,
  filterSupplierBalance,
  filterSupplierTurnover,
  supplierSort,
  suppliersSummary,
  formatCurrency,
  syncing,
  isMobile,
  suppliersPagination,
  supplierPage,
  supplierRowsPerPage,
  navigate,
  onSearchChange,
  onCategoryFilterChange,
  onPeriodChange,
  onBalanceFilterChange,
  onTurnoverFilterChange,
  onSortChange,
  onSyncGemi,
  onAdd,
  onEdit,
  onDelete,
  onSelectSupplier,
  onSelectAllSuppliers,
  onBulkDelete,
  onPageChange,
  onRowsPerPageChange,
}) {
  const turnoverLabel = PERIOD_LABELS[supplierPeriod] || 'περιόδου';

  return (
    <>
      {/* Stats */}
      <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: 2 }}>
        <Grid item xs={6} md={4}>
          <Card sx={{
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            boxShadow: '0 8px 32px rgba(239, 68, 68, 0.25)',
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                Σύνολο Υπολοίπων
              </Typography>
              <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#fff' }}>
                {formatCurrency(suppliersSummary.total_balance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={4}>
          <Card sx={{
            borderRadius: 3,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            boxShadow: '0 8px 32px rgba(139, 92, 246, 0.25)',
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                Τζίρος ({turnoverLabel})
              </Typography>
              <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#fff' }}>
                {formatCurrency(suppliersSummary.total_period_turnover)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, height: '100%',
            p: 2, borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(59,130,246,0.04) 0%, rgba(37,99,235,0.08) 100%)',
            border: '1px solid rgba(59,130,246,0.1)'
          }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 2,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <People sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {suppliersPagination.total || suppliers.length} Προμηθευτές
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Μετά τα ενεργά φίλτρα
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Paper sx={{
        p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
            <TextField
              size="small"
              placeholder="Αναζήτηση προμηθευτή..."
              value={supplierSearchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              sx={{ minWidth: { xs: 160, md: 220 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Κατηγορία</InputLabel>
              <Select
                value={filterSupplierCategory}
                label="Κατηγορία"
                onChange={(e) => onCategoryFilterChange(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">Όλες</MenuItem>
                {supplierCategories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cat.color }} />
                      {cat.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={onSyncGemi}
              disabled={syncing}
              startIcon={syncing ? <CircularProgress size={16} /> : null}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {syncing ? 'Ενημέρωση...' : 'ΓΕΜΗ Sync'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              onClick={onAdd}
            >
              Νέος Προμηθευτής
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Περίοδος τζίρου</InputLabel>
            <Select
              value={supplierPeriod}
              label="Περίοδος τζίρου"
              onChange={(e) => onPeriodChange(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="month">Τρέχων μήνας</MenuItem>
              <MenuItem value="3m">Τελευταίοι 3 μήνες</MenuItem>
              <MenuItem value="6m">Τελευταίοι 6 μήνες</MenuItem>
              <MenuItem value="12m">Τελευταίοι 12 μήνες</MenuItem>
              <MenuItem value="all">Όλα</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Υπόλοιπο</InputLabel>
            <Select
              value={filterSupplierBalance}
              label="Υπόλοιπο"
              onChange={(e) => onBalanceFilterChange(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">Όλοι</MenuItem>
              <MenuItem value="with_balance">Με υπόλοιπο</MenuItem>
              <MenuItem value="settled">Εξοφλημένοι</MenuItem>
              <MenuItem value="overdue">Καθυστερούν (&gt;30 ημ.)</MenuItem>
              <MenuItem value="high_balance">Υψηλό υπόλοιπο (&gt;€500)</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Τζίρος</InputLabel>
            <Select
              value={filterSupplierTurnover}
              label="Τζίρος"
              onChange={(e) => onTurnoverFilterChange(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">Όλοι</MenuItem>
              <MenuItem value="active">Ενεργοί στην περίοδο</MenuItem>
              <MenuItem value="high">Υψηλός τζίρος (&gt;€1.000)</MenuItem>
              <MenuItem value="inactive">Χαμηλός/αδρανείς (&lt;€100)</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Ταξινόμηση</InputLabel>
            <Select
              value={supplierSort}
              label="Ταξινόμηση"
              onChange={(e) => onSortChange(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="name">Όνομα (Α-Ω)</MenuItem>
              <MenuItem value="balance_desc">Υπόλοιπο (μεγαλύτερο)</MenuItem>
              <MenuItem value="turnover_desc">Τζίρος (μεγαλύτερος)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {selectedSuppliers.length > 0 && (
        <Box sx={{
          mb: 2, p: 2, borderRadius: 3,
          bgcolor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Επιλέχθηκαν {selectedSuppliers.length} προμηθευτές
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<Delete />}
            onClick={onBulkDelete}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Διαγραφή Επιλεγμένων
          </Button>
        </Box>
      )}

      {suppliers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary">Δεν βρέθηκαν προμηθευτές με τα επιλεγμένα φίλτρα.</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {suppliers.map((supplier) => (
            <Card key={supplier.id} variant="outlined">
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => navigate(`/suppliers/${supplier.id}`)}
                  >
                    {supplier.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {supplier.category && (
                      <Chip label={supplier.category.name} size="small" sx={{ bgcolor: supplier.category.color, color: '#fff', fontWeight: 500 }} />
                    )}
                    {supplier.has_overdue && (
                      <Chip label="Καθυστέρηση" size="small" color="warning" />
                    )}
                  </Box>
                </Box>

                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: 'rgba(139,92,246,0.06)', borderRadius: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                        Τζίρος ({turnoverLabel})
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                        {formatCurrency(supplier.period_turnover)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{
                      p: 1,
                      bgcolor: (supplier.total_balance || 0) > 0 ? 'rgba(239,68,68,0.06)' : 'rgba(34,197,94,0.06)',
                      borderRadius: 2, textAlign: 'center',
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                        Υπόλοιπο
                      </Typography>
                      <Typography variant="body2" sx={{
                        fontWeight: 700, fontSize: '0.8rem',
                        color: (supplier.total_balance || 0) > 0 ? '#dc2626' : '#16a34a',
                      }}>
                        {formatCurrency(supplier.total_balance)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {supplier.vat_number && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ΑΦΜ: {supplier.vat_number}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <IconButton onClick={() => navigate(`/suppliers/${supplier.id}`)} sx={{ width: 44, height: 44, borderRadius: '14px', border: '2px solid #0ea5e9', color: '#0ea5e9' }}>
                    <VisibilityOutlined sx={{ fontSize: 22 }} />
                  </IconButton>
                  <IconButton onClick={() => onEdit(supplier)} sx={{ width: 44, height: 44, borderRadius: '14px', border: '2px solid #64748b', color: '#64748b' }}>
                    <EditOutlined sx={{ fontSize: 22 }} />
                  </IconButton>
                  <IconButton onClick={() => onDelete(supplier)} sx={{ width: 44, height: 44, borderRadius: '14px', border: '2px solid #ef4444', color: '#ef4444' }}>
                    <DeleteOutlined sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={suppliers.length > 0 && selectedSuppliers.length === suppliers.length}
                    indeterminate={selectedSuppliers.length > 0 && selectedSuppliers.length < suppliers.length}
                    onChange={onSelectAllSuppliers}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Όνομα</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Κατηγορία</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }} align="right">
                  Τζίρος ({turnoverLabel})
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }} align="right">Υπόλοιπο</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>ΑΦΜ</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Όροι</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }} align="right">Ενέργειες</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.map((supplier) => (
                <TableRow key={supplier.id} selected={selectedSuppliers.includes(supplier.id)} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedSuppliers.includes(supplier.id)}
                      onChange={() => onSelectSupplier(supplier.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        component="span"
                        sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate(`/suppliers/${supplier.id}`)}
                      >
                        {supplier.name}
                      </Typography>
                      {supplier.has_overdue && (
                        <Chip label="Καθυστέρηση" size="small" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {supplier.category ? (
                      <Chip label={supplier.category.name} size="small" sx={{ bgcolor: supplier.category.color, color: '#fff', fontWeight: 500 }} />
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <TrendingUp sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(supplier.period_turnover)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <AccountBalance sx={{ fontSize: 16, color: (supplier.total_balance || 0) > 0 ? '#dc2626' : '#16a34a' }} />
                      <Typography variant="body2" sx={{
                        fontWeight: 700,
                        color: (supplier.total_balance || 0) > 0 ? '#dc2626' : '#16a34a',
                      }}>
                        {formatCurrency(supplier.total_balance)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{supplier.vat_number || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={supplier.payment_terms === 'cash' ? 'Μετρητά' : 'Πίστωση'}
                      color={supplier.payment_terms === 'cash' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/suppliers/${supplier.id}`)} title="Προβολή"
                        sx={{ bgcolor: 'rgba(14,165,233,0.08)', borderRadius: 2 }}>
                        <Visibility sx={{ fontSize: 18, color: '#0ea5e9' }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => onEdit(supplier)} title="Επεξεργασία"
                        sx={{ bgcolor: 'rgba(100,116,139,0.08)', borderRadius: 2 }}>
                        <Edit sx={{ fontSize: 18, color: '#64748b' }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => onDelete(supplier)}
                        sx={{ bgcolor: 'rgba(239,68,68,0.08)', borderRadius: 2 }}>
                        <Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={suppliersPagination.total || 0}
        page={supplierPage}
        onPageChange={onPageChange}
        rowsPerPage={supplierRowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 50, 100]}
        labelRowsPerPage="Εμφάνιση ανά:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} από ${count !== -1 ? count : `περισσότερα από ${to}`}`}
      />
    </>
  );
}
